import { computed, ref, watch } from 'vue'

import { useAuthStore } from '@/stores/auth.store'
import { useClaudeFleetRunnersStore } from '@/stores/claude-fleet-runners.store'

import { usePollWhile } from '@/composables/usePollWhile'
import { useStoreErrorWatch } from '@/composables/useStoreErrorWatch'
import { useToast } from '@/composables/useToast'

import {
  myRunner,
  RUNNER_START_URL,
  runnerActionRequest,
  runnerChoiceText,
  runnerCardControls,
  runnerCardPauseChoices,
  runnerCardView
} from '@/services/claude-fleet'
import type { RunnerCardControlKey, RunnerCardPauseKey } from '@/services/claude-fleet'

/** A heartbeat lands once a minute, so reading more often shows nothing new. */
const POLL_MS = 60_000
/** While Starting, the first heartbeat is what the engineer is waiting for, so the card turns live within seconds of it. */
const STARTING_POLL_MS = 10_000

export const START_HINT =
  'Nothing opened? Start needs the workspace link handler, installed once: bin/install-continue-handler.sh (Windows: bin\\install-continue-handler.ps1). Or run bin/runner.sh from your tattle-claude-workspace folder.'

/** The signed-in engineer's own runner for the sidebar runner card, read with the Runners tab's list and kept fresh. */
export function useMyRunnerCard() {
  const authStore = useAuthStore()
  const runnersStore = useClaudeFleetRunnersStore()
  useStoreErrorWatch(runnersStore, 'Your runner')
  const { showInfo, showSuccess } = useToast()

  const now = ref(Date.now())
  const startedAt = ref<number | null>(null)

  async function refresh() {
    now.value = Date.now()
    await runnersStore.fetchRunners({ background: true })
  }

  usePollWhile(ref(true), refresh, POLL_MS)
  // The Runners tab resets the store when it closes, so the card reads the list again rather than waiting a minute.
  watch(
    () => runnersStore.loaded,
    isLoaded => {
      if (!isLoaded) void refresh()
    },
    { immediate: true }
  )

  const runner = computed(() => myRunner(runnersStore.runners, authStore.user?.email))
  const view = computed(() =>
    runnerCardView(runner.value, { now: now.value, startedAt: startedAt.value })
  )

  usePollWhile(
    computed(() => view.value.state === 'starting'),
    refresh,
    STARTING_POLL_MS
  )

  const controls = computed(() =>
    runnerCardControls(runner.value, view.value, {
      now: now.value,
      sending: Boolean(runner.value && runnersStore.actionMachine === runner.value.machine)
    })
  )

  function start() {
    startedAt.value = Date.now()
    now.value = startedAt.value
    window.location.assign(RUNNER_START_URL)
    showInfo(START_HINT, 'Starting your runner')
  }

  const pauseChoices = computed(() =>
    runner.value ? runnerCardPauseChoices(runner.value, now.value) : []
  )

  /**
   * Start opens the runner here, and cancel forgets it was clicked; resume and each pause choice go to the fleet at
   * once, as on the runner menu.
   */
  async function act(key: Exclude<RunnerCardControlKey, 'pause'> | RunnerCardPauseKey) {
    if (key === 'start') {
      start()
      return
    }
    if (key === 'cancel') {
      startedAt.value = null
      return
    }
    const machine = runner.value?.machine
    if (!machine) return
    if (await runnersStore.sendAction(machine, runnerActionRequest(key))) {
      showSuccess(runnerChoiceText(key, 'Your').done, 'Your runner')
    }
  }

  // Only engineering sees the card, so CS, sales and product never meet a runner they cannot use: someone on the
  // Engineering Team page, or whose machine is already a runner (all the fleet has while it cannot read the page). A
  // viewer the fleet refuses never gets a list at all.
  return {
    runner,
    view,
    controls,
    pauseChoices,
    visible: computed(() => runnersStore.loaded && (runnersStore.viewerEngineer === true || runner.value !== null)),
    start,
    act
  }
}
